<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  watchEffect,
  provide,
} from 'vue';
import { useRoute } from 'vue-router';
import { useStyleTag, useTitle } from '@vueuse/core';

import ButtonElement from '@/components/ButtonElement.vue';
import ImageElement from '@/components/ImageElement.vue';
import TextElement from '@/components/TextElement.vue';
import ShareItElement from '@/components/ShareItElement.vue';
import EmbedElement from '@/components/EmbedElement.vue';
import ShoppableElement from '@/components/ShoppableElement.vue';

import useSpacing from '@/composables/useSpacing';

const route = useRoute();
const isEditing = ref(route.meta.editing || false);
const isPreview = ref(route.meta.preview || false);

const pageData = ref(route.params.pageData);
const fontsData = ref(route.params.fontsData);
const affiliateData = ref(route.params.affiliateData);

const editingData = ref({
  page: { settings: {}, elements: [], owner: null },
  fonts: [],
  affiliates: {},
});

const receiveMessage = (event) => {
  const { type, data } = event.data;
  if (type === 'page-builder-update') {
    editingData.value = data;
  }
};

onMounted(() => {
  if (isEditing.value) {
    window.addEventListener('message', receiveMessage);
    window.parent.postMessage({ type: 'pages-preview-ready' }, '*');
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('message', receiveMessage);
});

const page = computed(() => (!isEditing.value ? pageData.value : editingData.value.page));
const fonts = computed(() => (!isEditing.value ? fontsData.value : editingData.value.fonts));
const affiliates = computed(() => (!isEditing.value ? affiliateData.value : editingData.value.affiliates));

provide('page', page);
provide('affiliates', affiliates);

const pageSettings = computed(() => page.value.settings ?? {});
const pageElements = computed(() => page.value.elements?.filter((el) => el.settings.visible) ?? []);

const hasShoppable = computed(() => pageElements.value.some((el) => el.type === 'shoppable'));

const { margin } = useSpacing(pageSettings);

const background = computed(() => {
  if (pageSettings?.value.backgroundType === 'image' && pageSettings?.value.background) {
    return `url(${pageSettings?.value.background})`;
  }

  return pageSettings?.value.background ?? 'var(--body-bg-color)';
});

const styles = computed(() => `.body {
  background: ${background.value || 'var(--body-bg-color)'} no-repeat center center fixed;
  background-size: cover;
  min-height: 100vh;
  margin: ${margin.value};
  padding-bottom: ${hasShoppable.value ? '50px' : '0'};
}`);
const title = computed(() => pageSettings?.value.title ?? 'Landing Page');
const description = computed(() => pageSettings?.value.description ?? 'Landing Page');
const contentMaxWidth = computed(() => `${pageSettings?.value.contentMaxWidth ?? 800}px`);

useTitle(title); // document title will match the page title

const setMetaDescription = () => {
  const metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription) {
    metaDescription.setAttribute('content', description.value);
  }
};

watchEffect(() => {
  if (description.value) {
    setMetaDescription();
  }
});

useStyleTag(styles);

const fontsStyles = ref('');

watchEffect(() => {
  if (fonts.value?.length) {
    fontsStyles.value = fonts.value.map((font) => `
        @font-face {
          font-family: '${font._id}';
          src: url('${font.alt ? font.alt.media_url : font.media_url}');
          font-weight: 'normal';
          font-style: 'normal';
          font-display: block;
        }
      `).join('\n');
  }
});

useStyleTag(fontsStyles);

const elementName = (type) => {
  switch (type) {
    case 'button':
      return ButtonElement;
    case 'image':
      return ImageElement;
    case 'text':
      return TextElement;
    case 'share-it':
      return ShareItElement;
    case 'embed':
      return EmbedElement;
    case 'shoppable':
      return ShoppableElement;
    default:
      return 'div';
  }
};

// Later you can modify styles
// css.value = '.foo { margin-top: 64px; }'

const pageStyle = computed(() => ({
  pointerEvents: isEditing.value ? 'none' : undefined,
}));
</script>

<template>
  <div
    :class="{
      'landing-page': true,
      'landing-page--preview': isPreview,
    }"
    :style="pageStyle"
  >
    <div
      v-if="isPreview"
      class="preview-header drop-shadow-md"
      data-test="pages-preview-header"
    >
      You are viewing a Preview
    </div>

    <template
      v-for="{ id, type, settings } in pageElements"
      :key="id"
    >
      <component
        :is="elementName(type)"
        :settings="settings"
      />
    </template>
  </div>

  <footer class="landing-page-footer">
    <p
      v-if="hasShoppable"
      class="shoppable-disclaimer"
    >We may earn a commission if you purchase through the link(s) on this page. All prices are as is and are subject to change.</p>
  </footer>
</template>

<style scoped lang="scss">
$preview-header-height: 32px;

/* Content Container of Landing Page */
.landing-page {
  width: 100%;
  max-width: v-bind(contentMaxWidth);
  margin: 0 auto;
  padding: 1.25rem 2.5rem;

  &----preview {
    margin-top: $preview-header-height;
  }
}

.landing-page-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  background-color: transparent;
  z-index: 9000;
}

.shoppable-disclaimer {
  text-align: center;
  font-size: 0.8rem;
  padding: 10px;
  background-color: white;
}

.preview-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background-color: white;
  color: black;
  height: $preview-header-height;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
